import { Button } from "@benefit-systems/common-components";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Loading from "web/Layout/Common/Loading";
import __ from "web/Layout/Translations";

import useInternetConnection from "web/hooks/useInternetConnection/useInternetConnection";

import { SupplierID } from "web/types/SupplierData";

import { useGetSupplierDataQuery } from "web/features/customer/customerApiSlice";

import DownloadCodeButton from "./DownloadCodeButton";
import classes from "./codeModal.scss";
import copyCodeToClipboard from "./utils/copyCodeToClipboard";
import getBarcode from "./utils/getBarcode";
import getCode from "./utils/getCode";
import handleCodeSessionStorageAction, {
  CodeActionType,
} from "./utils/handleCodeSessionStorageAction";
import printCode from "./utils/printCode";
import printCodeDirectly from "./utils/printCodeDirectly";

interface ICodeModalContainerProps {
  productName: string;
  itemId: number;
  supplierId: SupplierID;
  orderPaid: boolean;
}

const CodeModalContainer: FC<ICodeModalContainerProps> = ({
  productName,
  itemId,
  supplierId,
  orderPaid,
}) => {
  const dispatch = useDispatch();

  const [code, setCode] = useState(
    handleCodeSessionStorageAction({ action: CodeActionType.GET_CODE, itemId })
  );
  const [barcode, setBarcode] = useState(
    handleCodeSessionStorageAction({
      action: CodeActionType.GET_BARCODE,
      itemId,
    })
  );
  const [canPrintCode, setCanPrintCode] = useState(
    !!handleCodeSessionStorageAction({
      action: CodeActionType.GET_CAN_PRINT,
      itemId,
    })
  );
  const [isPrinting, setIsPrinting] = useState(false);

  const codeInputRef = useRef<HTMLInputElement>(null);
  const { data: supplierData, isLoading: loading } =
    useGetSupplierDataQuery(supplierId);

  const { isDisconnectedWebView } = useInternetConnection();

  // eslint-disable-next-line no-underscore-dangle
  const _code = code || "";
  const supplierSettingCanPrintCode = supplierData?.settings?.canPrintCode;

  useEffect(() => {
    if (!canPrintCode && supplierSettingCanPrintCode) {
      handleCodeSessionStorageAction({
        itemId,
        action: CodeActionType.SET_CAN_PRINT,
        value: supplierSettingCanPrintCode,
      });
      setCanPrintCode(supplierSettingCanPrintCode);
    }
  }, [canPrintCode, supplierSettingCanPrintCode]);

  const onCopyCodeToClipboardHandler = () =>
    copyCodeToClipboard({ dispatch, codeInputRef, code: _code });

  useEffect(() => {
    if (canPrintCode && !barcode && orderPaid) {
      getBarcode({ itemId, setBarcode });
    }
  }, [itemId, canPrintCode, barcode, orderPaid, getBarcode]);

  useEffect(() => {
    if (!code && orderPaid) {
      getCode({ itemId, setCode });
    }
  }, [itemId, code, orderPaid, getCode]);

  const print = useCallback(
    () => printCode({ itemId, setIsPrinting }),
    [itemId]
  );

  const onPrintCodeDirectlyHandler = () =>
    printCodeDirectly({ productName, code });

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.buttons}>
          <DownloadCodeButton
            hasCode={!!code}
            canPrintCode={canPrintCode}
            isPrinting={isPrinting}
            onPrintHandler={print}
            onPrintCodeDirectlyHandler={onPrintCodeDirectlyHandler}
          />
        </div>
        <div className={classes.title} data-t1="code_modal_title">
          <h2 className={classes.header} data-t1="code_modal_header">
            {__("Twój kod")}
          </h2>
          <h2 className={classes.productName} data-t1="code_modal_product_name">
            {productName}
          </h2>
        </div>
        <div className={classes.box}>
          {loading ? (
            <Loading />
          ) : (
            <>
              {barcode && canPrintCode ? (
                <div className={classes.codeImageContainer}>
                  <img
                    className={classes.codeImage}
                    src={barcode}
                    alt={__("Kod kreskowy")}
                  />
                </div>
              ) : null}
              {code ? (
                <div className={classes.code} data-t1="code_modal_code">
                  {code}
                </div>
              ) : null}
              {isDisconnectedWebView && (!barcode || !code) && (
                <p>{__("Brak połączenia z siecią")}</p>
              )}
            </>
          )}

          <form className={classes.copy} data-t1="code_modal_copy">
            <input
              ref={codeInputRef}
              value={_code}
              className="visually-hidden"
              data-t1="code_modal_input"
              readOnly
            />
            <Button
              variant="secondary"
              onClick={onCopyCodeToClipboardHandler}
              dataT1="code_modal_button"
              transparent
            >
              {__("Kopiuj kod")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CodeModalContainer;
